import React, { useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { KYB_PATH, KYB_STATUS } from "../../utils/kyb";

// One band per business verification (KYB) status — src/utils/kyb.js.
// "approved" has no band: the customer can book. Every CTA opens the business
// verification flow (KYB_PATH), the same target as the KYC menu item.
const AMBER = {
  bg: "#FEF3E2",
  borderColor: "#F5A623",
  accentBar: "#F5A623",
  titleColor: "#e10101",
  subColor: "#e10101",
  iconBg: "#F5A623",
};
const BLUE = {
  bg: "#EEF6FF",
  borderColor: "#378ADD",
  accentBar: "#378ADD",
  titleColor: "#0C447C",
  subColor: "#185FA5",
  iconBg: "#378ADD",
};
const RED = {
  bg: "#FEF0EF",
  borderColor: "#E24B4A",
  accentBar: "#E24B4A",
  titleColor: "#7A1F1F",
  subColor: "#A32D2D",
  iconBg: "#E24B4A",
};

const RIBBON_CONFIG = {
  [KYB_STATUS.NOT_STARTED]: {
    ...AMBER,
    icon: "warning",
    title: "Business Verification Pending – Please verify your business to start booking.",
    sub: "Bookings are disabled until your business is verified. It takes about five minutes.",
    cta: "Verify business",
  },
  [KYB_STATUS.IN_PROGRESS]: {
    ...AMBER,
    icon: "warning",
    title: "Business verification incomplete – finish where you left off to start booking.",
    sub: "Bookings are disabled until your business is verified.",
    cta: "Continue verification",
  },
  [KYB_STATUS.IN_REVIEW]: {
    ...BLUE,
    icon: "clock",
    title: "Business verification under review — our team is checking your details.",
    sub: "This usually takes 1 working day. Bookings open as soon as it's approved.",
    cta: "View status",
  },
  [KYB_STATUS.DOCUMENTS_REQUESTED]: {
    ...AMBER,
    icon: "warning",
    title: "Documents needed – our team asked for more documents to verify your business.",
    sub: "Upload them to continue. Bookings open once they're reviewed.",
    cta: "Upload documents",
  },
  [KYB_STATUS.PAUSED]: {
    ...RED,
    icon: "cross",
    title: "Bookings paused — your business verification is being reviewed again.",
    sub: "Open Business verification to see what's needed.",
    cta: "See what's needed",
  },
  [KYB_STATUS.DECLINED]: {
    ...RED,
    icon: "cross",
    title: "Business verification declined — bookings are disabled.",
    sub: "Check the reason, then start again or contact support.",
    cta: "Restart verification",
  },
};

const WarningIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="white">
    <path d="M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z" />
  </svg>
);
const ClockIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="white">
    <path d="M11.99 2C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2zM12 20c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8zm.5-13H11v6l5.25 3.15.75-1.23-4.5-2.67V7z" />
  </svg>
);
const CrossIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="white">
    <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
  </svg>
);

const ICONS = {
  warning: <WarningIcon />,
  clock: <ClockIcon />,
  cross: <CrossIcon />,
};

/**
 * @param {string} kybStatus  state.usersection.kyb_verification_status
 * @param {string} [reason]   state.usersection.kyb_status_reason — why bookings
 *                            are paused / why it was declined; replaces the
 *                            default sub line when present
 */
const KycStatusRibbon = ({ kybStatus, reason, onHeightChange }) => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const ref = useRef(null);
  const config = RIBBON_CONFIG[kybStatus];

  useEffect(() => {
    // Always report 0 if no ribbon
    if (!config || !ref.current) {
      onHeightChange?.(0);
      return;
    }
    const ro = new ResizeObserver(([entry]) => {
      onHeightChange?.(entry.contentRect.height);
    });
    ro.observe(ref.current);
    return () => ro.disconnect();
  }, [kybStatus]);

  if (!config) return null;

  const showReason = !!reason && (kybStatus === KYB_STATUS.PAUSED || kybStatus === KYB_STATUS.DECLINED);
  // Already on the business verification page: the button would go nowhere.
  const showCta = !pathname.startsWith(KYB_PATH);

  return (
    <div
      ref={ref}
      role="alert"
      aria-live="polite"
      style={{
        // position: "fixed",
        position: "relative",
        top: 0,
        left: "0%",
        right: "0%",
        zIndex: 10,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexWrap: "wrap",
        gap: "8px",
        // left accent bar matching your app's card style
        // borderLeft: `4px solid ${config.accentBar}`,
        // borderBottom: `1px solid ${config.borderColor}`,
        border: `1px solid ${config.borderColor}`,
        borderRadius: "8px",
        backgroundColor: config.bg,
        padding: "7px 20px 7px 16px",
        minHeight: "44px",
      }}
    >
      {/* Left: icon + text */}
      <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
        <div style={{
          width: 26,
          height: 26,
          borderRadius: "50%",
          backgroundColor: config.iconBg,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}>
          {ICONS[config.icon]}
        </div>
        <div>
          <div style={{
            fontSize: "13px",
            fontWeight: 600,
            color: config.titleColor,
            lineHeight: "1.35",
          }}>
            {config.title}
          </div>
          <div style={{
            fontSize: "11px",
            color: config.subColor,
            marginTop: "1px",
            lineHeight: "1.35",
          }}>
            {showReason ? `Reason: ${reason}` : config.sub}
          </div>
        </div>
      </div>

      {/* Right: CTA button — opens the business verification flow. Its
          background is the band's own accent colour (config.accentBar), so it
          always matches the strip — amber, blue or red. White text keeps it
          readable across those accents. */}
      {showCta && (
        <button
          onClick={() => navigate(KYB_PATH)}
          style={{
            fontSize: "12px",
            fontWeight: 700,
            padding: "7px 18px",
            borderRadius: "8px",
            border: "none",
            backgroundColor: config.accentBar,
            color: "#ffffff",
            cursor: "pointer",
            whiteSpace: "nowrap",
            flexShrink: 0,
            lineHeight: "1.4",
          }}
        >
          {config.cta}
        </button>
      )}
    </div>
  );
};

export default KycStatusRibbon;
